import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FaRegStickyNote, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import Select from "react-select";
import { Link } from "react-router-dom";
import { useAuth } from './AuthContext';
import { rootUrl } from '../../config/config';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '34px',
        fontSize: '12px',
        borderColor: state.isFocused ? '#FF5733' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 1px #FF5733' : 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '12px',
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#FFF1EB' : '#fff',
        color: state.isSelected ? '#fff' : '#1e293b',
    }),
    menu: (base) => ({ ...base, zIndex: 30 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: '34px' }),
};

const fmt = (v) => Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtKes = (v) => `KES ${fmt(v)}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

const TYPE_COLOURS = {
    WKI: 'bg-green-100 text-green-700',
    WKR: 'bg-blue-100 text-blue-700',
    EXP: 'bg-red-100 text-red-700',
    ADJ: 'bg-amber-100 text-amber-700',
    WAR: 'bg-purple-100 text-purple-700',
};
const typeBadgeClass = (t) => TYPE_COLOURS[String(t || '').toUpperCase()] || 'bg-slate-100 text-slate-600';

const PagBtn = ({ children, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled}
        className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#FF5733] hover:bg-[#e64520] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors">
        {children}
    </button>
);

const LIMIT = 10;

const AllTransactions = () => {
    const { token } = useAuth();
    const getHeaders = () => ({ Authorization: `Bearer ${token || localStorage.getItem('authToken')}` });

    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState([]);
    const [noteModal, setNoteModal] = useState(null);
    const [error, setError] = useState('');
    const [type, setType] = useState('');
    const [car, setCar] = useState('');
    const [refFilter, setRefFilter] = useState('');
    const [weekFilter, setWeekFilter] = useState('');
    const [owner, setOwner] = useState('');
    const [cars, setCars] = useState([]);
    const [weekOptions, setWeekOptions] = useState([]);
    const [types, setTypes] = useState([]);
    const [owners, setOwners] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 });
    const [loading, setLoading] = useState(false);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchTransactions = useCallback(async (page = 1) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
            if (dateFrom) params.append('dateFrom', dateFrom);
            if (dateTo) params.append('dateTo', dateTo);
            if (type) params.append('type', type);
            if (car) params.append('car', car);
            if (refFilter) params.append('ref', refFilter);
            if (weekFilter) params.append('week', weekFilter);
            if (owner) params.append('owner', owner);

            const res = await axios.get(`${rootUrl}/all_transactions?${params}`, { headers: getHeaders() });
            setTransactions(res.data.transactions || []);
            setSummary(res.data.summary || []);
            setPagination(res.data.pagination || { currentPage: page, totalPages: 1, totalRecords: 0 });
            setCurrentPage(page);
        } catch (e) {
            const msg = e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to load transactions';
            setError(msg);
            setTransactions([]);
            setSummary([]);
        } finally {
            setLoading(false);
        }
    }, [token, dateFrom, dateTo, type, car, owner, refFilter, weekFilter]);

    useEffect(() => { fetchTransactions(1); }, [fetchTransactions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const h = getHeaders();
        axios.get(`${rootUrl}/cars`, { headers: h }).then(r => setCars(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        axios.get(`${rootUrl}/owners/list`, { headers: h }).then(r => setOwners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        axios.get(`${rootUrl}/payments/types/car`, { headers: h }).then(r => setTypes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        axios.get(`${rootUrl}/system/valid_weeks?past=24`, { headers: h }).then(r => setWeekOptions(r.data?.valid_weeks || [])).catch(() => {});
    }, [token]);

    const typeOptions    = [{ value: '', label: 'All Types' },  ...types.map(t => ({ value: t.type, label: t.short_desc || t.type }))];
    const carOptions     = [{ value: '', label: 'All Cars' },   ...cars.map(c => ({ value: c.plate_no, label: c.plate_no }))];
    const ownerOptions   = [{ value: '', label: 'All Owners' }, ...owners.map(o => ({ value: o.code || o.name, label: o.name }))];
    const weekSelectOpts = [{ value: '', label: 'All Weeks' },  ...weekOptions.map(w => ({ value: w, label: w }))];

    const handlePageChange = (page) => fetchTransactions(page);

    const totalIncome  = summary.filter(s => ['WKI'].includes(s.type?.toUpperCase())).reduce((a, s) => a + Number(s.total_amount || 0), 0);
    const totalExpense = summary.filter(s => ['EXP', 'WKR'].includes(s.type?.toUpperCase())).reduce((a, s) => a + Number(s.total_amount || 0), 0);
    const totalAll     = summary.reduce((a, s) => a + Number(s.total_amount || 0), 0);
    const from = pagination.totalRecords === 0 ? 0 : (currentPage - 1) * LIMIT + 1;
    const to   = Math.min(currentPage * LIMIT, pagination.totalRecords);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="mx-auto max-w-7xl px-4 py-6 pb-10">

                {/* Header */}
                <div className="mb-5">
                    <h1 className="text-2xl font-bold text-[#2a2e30]">All Transactions</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Car-level income and expense records across the fleet</p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm p-4 mb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">From</label>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">To</label>
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Type</label>
                            <Select options={typeOptions} value={typeOptions.find(o => o.value === type) || null} onChange={o => setType(o?.value || '')} styles={selectStyles} placeholder="All" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Car</label>
                            <Select options={carOptions} value={carOptions.find(o => o.value === car) || null} onChange={o => setCar(o?.value || '')} styles={selectStyles} placeholder="All" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Owner</label>
                            <Select options={ownerOptions} value={ownerOptions.find(o => o.value === owner) || null} onChange={o => setOwner(o?.value || '')} styles={selectStyles} placeholder="All" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Week</label>
                            <Select options={weekSelectOpts} value={weekSelectOpts.find(o => o.value === weekFilter) || null} onChange={o => setWeekFilter(o?.value || '')} styles={selectStyles} placeholder="All" isClearable />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Ref</label>
                            <input type="text" value={refFilter} onChange={e => setRefFilter(e.target.value)} placeholder="Search…"
                                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]" />
                        </div>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
                        <FaExclamationTriangle className="shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => fetchTransactions(1)} className="ml-auto text-xs font-semibold underline hover:no-underline">Retry</button>
                    </div>
                )}

                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Records</p>
                        <p className="text-2xl font-bold text-[#2a2e30]">{loading ? '—' : pagination.totalRecords.toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Amount</p>
                        <p className="text-lg font-bold text-[#2a2e30]">{loading ? '—' : fmtKes(totalAll)}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-green-100 shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Income (WKI)</p>
                        <p className="text-lg font-bold text-green-700">{loading ? '—' : fmtKes(totalIncome)}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-red-100 shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Deductions</p>
                        <p className="text-lg font-bold text-red-600">{loading ? '—' : fmtKes(totalExpense)}</p>
                    </div>
                </div>

                {/* Type breakdown strip */}
                {summary.length > 0 && (
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-5 py-3 mb-4 overflow-x-auto">
                        <div className="flex items-center gap-6 min-w-max">
                            {summary.map(s => (
                                <div key={s.type} className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadgeClass(s.type)}`}>{s.type}</span>
                                    <span className="text-xs text-gray-500">{Number(s.count).toLocaleString()} txn</span>
                                    <span className="text-xs font-semibold text-[#2a2e30]">{fmtKes(s.total_amount)}</span>
                                </div>
                            ))}
                            {loading && <span className="text-xs text-[#FF5733] font-semibold animate-pulse">Updating…</span>}
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                        <table className="min-w-[760px] w-full text-left">
                            <thead>
                                <tr className="border-b border-[#EFE3DB] bg-[#FFF8F5]">
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Plate / Doc</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type</th>
                                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Week</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Driver</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Ref / Notes</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">By</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && transactions.length === 0 && (
                                    <tr><td colSpan="9" className="px-4 py-10 text-center text-sm text-gray-400">
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-[#FF5733] border-t-transparent rounded-full animate-spin" />
                                            Loading transactions…
                                        </div>
                                    </td></tr>
                                )}
                                {!loading && transactions.length === 0 && (
                                    <tr><td colSpan="9" className="px-4 py-10 text-center text-sm text-gray-400">No transactions found for the selected filters.</td></tr>
                                )}
                                {transactions.map((tx) => (
                                        <tr key={tx.id} className={`border-b border-slate-50 text-xs transition-colors hover:bg-orange-50/50 hover:border-l-2 hover:border-l-[#FF5733] ${loading ? 'opacity-60' : ''}`}>
                                            <td className="px-4 py-2.5">
                                                <Link to={`/cars/${tx.plate_no}`} className="font-mono font-bold text-[#FF5733] hover:underline">{tx.plate_no}</Link>
                                                {tx.doc_no && <p className="text-[10px] text-gray-400 font-mono mt-0.5">{tx.doc_no}</p>}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadgeClass(tx.type)}`}>{tx.type}</span>
                                                {tx.short_desc && <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[80px]">{tx.short_desc}</p>}
                                            </td>
                                            <td className="px-4 py-2.5 text-right font-semibold text-slate-800 font-mono tabular-nums">{fmt(tx.amount)}</td>
                                            <td className="px-4 py-2.5 text-slate-600">{formatDate(tx.date)}</td>
                                            <td className="px-4 py-2.5 text-slate-600 font-mono">{tx.week || '-'}</td>
                                            <td className="px-4 py-2.5 text-slate-600">{tx.driver_name ? tx.driver_name.trim().split(' ')[0] : '-'}</td>
                                            <td className="px-4 py-2.5 max-w-[160px]">
                                                {tx.your_ref && <p className="truncate text-slate-600">{tx.your_ref}</p>}
                                                {tx.notes && (
                                                    <button onClick={() => setNoteModal(tx.notes)} className="flex items-center gap-1 text-[#FF5733] hover:underline mt-0.5">
                                                        <FaRegStickyNote className="w-3 h-3 shrink-0" />
                                                        <span className="truncate max-w-[120px]">{tx.notes}</span>
                                                    </button>
                                                )}
                                                {!tx.your_ref && !tx.notes && <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-4 py-2.5 text-slate-500">{tx.input_by || '-'}</td>
                                            <td className="px-4 py-2.5">
                                                <Link to={`/transactions/edit/${tx.id}`} className="text-xs font-semibold text-[#FF5733] hover:underline">Edit</Link>
                                            </td>
                                        </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between gap-3 bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-5 py-3">
                        <span className="text-xs text-gray-500">{from}–{to} of {pagination.totalRecords.toLocaleString()}</span>
                        <div className="flex items-center gap-1">
                            <PagBtn onClick={() => handlePageChange(1)} disabled={currentPage === 1 || loading}>&laquo;</PagBtn>
                            <PagBtn onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading}>&lsaquo;</PagBtn>
                            <span className="px-3 py-1.5 text-sm font-semibold text-[#2a2e30]">{currentPage} / {pagination.totalPages}</span>
                            <PagBtn onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pagination.totalPages || loading}>&rsaquo;</PagBtn>
                            <PagBtn onClick={() => handlePageChange(pagination.totalPages)} disabled={currentPage >= pagination.totalPages || loading}>&raquo;</PagBtn>
                        </div>
                    </div>
                )}
            </div>

            {/* Note modal */}
            {noteModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setNoteModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-[#2a2e30] flex items-center gap-2">
                                <FaRegStickyNote className="text-[#FF5733]" /> Note
                            </h3>
                            <button onClick={() => setNoteModal(null)} className="text-gray-400 hover:text-gray-600 transition"><FaTimes /></button>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{noteModal}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllTransactions;
